import React from 'react';
import { Check, ArrowRight, Users, Clock, MessageCircle, Shield, Target } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function ConflictCoaching() {
  const {
    t
  } = useLanguage();
  const benefits = t('conflictCoaching.benefits') as string[];
  const curriculum = t('conflictCoaching.curriculum') as Array<{
    day: string;
    title: string;
    topics: string[];
  }>;
  const forWhomItems = t('conflictCoaching.forWhomItems') as string[];
  const forWhomIcons = [<Shield className="text-coral shrink-0" size={20} data-id="element-480" />, <Users className="text-coral shrink-0" size={20} data-id="element-481" />, <Target className="text-coral shrink-0" size={20} data-id="element-482" />];
  return <PageTransition data-id="element-483">
      <div className="pt-12 pb-24" data-id="element-484">
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-485">
          <DecorativeShape type="blob" color="bg-coral/10" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-486" />
          <DecorativeShape type="circle" color="bg-teal/10" className="bottom-0 left-0 w-64 h-64 -translate-x-1/4 translate-y-1/4 -z-10" data-id="element-487" />
          <Animate className="text-center max-w-4xl mx-auto" data-id="element-488">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-489">
              {t('conflictCoaching.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-490">
              {t('conflictCoaching.title')} <br data-id="element-491" />
              <span className="text-coral" data-id="element-492">
                {t('conflictCoaching.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-493">
              {t('conflictCoaching.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm font-medium text-gray-500 mb-10" data-id="element-494">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-495">
                <Clock size={18} className="text-coral" data-id="element-496" />
                <span data-id="element-497">{t('conflictCoaching.duration')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-498">
                <Users size={18} className="text-coral" data-id="element-499" />
                <span data-id="element-500">{t('conflictCoaching.audience')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-501">
                <MessageCircle size={18} className="text-coral" data-id="element-502" />
                <span data-id="element-503">{t('conflictCoaching.format')}</span>
              </div>
            </div>
            <Link to="/contact" data-id="element-504">
              <Button size="lg" rightIcon={<ArrowRight size={20} data-id="element-506" />} data-id="element-505">
                {t('conflictCoaching.bookCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-507">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-508">
            <div className="lg:col-span-2 space-y-12" data-id="element-509">
              <Animate data-id="element-510">
                <h2 className="text-3xl font-bold mb-6 text-charcoal" data-id="element-511">
                  {t('conflictCoaching.aboutTitle')}
                </h2>
                <div className="prose prose-lg text-gray-600" data-id="element-512">
                  <p className="mb-4" data-id="element-513">{t('conflictCoaching.aboutP1')}</p>
                  <p data-id="element-514">{t('conflictCoaching.aboutP2')}</p>
                </div>
              </Animate>
              <Animate delay={0.1} data-id="element-515">
                <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-516">
                  {t('conflictCoaching.learnTitle')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-517">
                  {benefits.map((benefit: string, index: number) => <div key={index} className="flex items-start gap-3" data-id="element-518">
                      <div className="mt-1 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-519">
                        <Check size={14} strokeWidth={3} data-id="element-520" />
                      </div>
                      <span className="text-gray-700" data-id="element-521">{benefit}</span>
                    </div>)}
                </div>
              </Animate>
              <Animate delay={0.15} data-id="element-522">
                <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-523">
                  {t('conflictCoaching.structureTitle')}
                </h3>
                <div className="space-y-6" data-id="element-524">
                  {curriculum.map((item, index) => <Card key={index} className="p-6 border-l-4 border-coral bg-gray-50" data-id="element-525">
                      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4" data-id="element-526">
                        <h4 className="text-xl font-bold text-charcoal" data-id="element-527">
                          {item.title}
                        </h4>
                        <span className="text-sm font-bold text-coral uppercase tracking-wide bg-white px-3 py-1 rounded-full shadow-sm mt-2 md:mt-0 w-fit" data-id="element-528">
                          {item.day}
                        </span>
                      </div>
                      <ul className="space-y-2" data-id="element-529">
                        {item.topics.map((topic: string, i: number) => <li key={i} className="flex items-center gap-2 text-gray-600" data-id="element-530">
                            <div className="w-1.5 h-1.5 rounded-full bg-coral" data-id="element-531" />
                            {topic}
                          </li>)}
                      </ul>
                    </Card>)}
                </div>
              </Animate>
            </div>
            <div className="lg:col-span-1" data-id="element-532">
              <div className="sticky top-24 space-y-8" data-id="element-533">
                <Card className="p-8 bg-white shadow-xl border-t-4 border-coral" data-id="element-534">
                  <h3 className="text-xl font-bold mb-6 text-charcoal" data-id="element-535">
                    {t('conflictCoaching.forWhom')}
                  </h3>
                  <ul className="space-y-4 mb-8" data-id="element-536">
                    {forWhomItems.map((item: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-537">
                        {forWhomIcons[i]}
                        <span className="text-gray-600" data-id="element-538">{item}</span>
                      </li>)}
                  </ul>
                  <Link to="/contact" className="block" data-id="element-539">
                    <Button className="w-full" rightIcon={<ArrowRight size={18} data-id="element-541" />} data-id="element-540">
                      {t('conflictCoaching.inquiry')}
                    </Button>
                  </Link>
                </Card>
                <div className="bg-coral/5 rounded-3xl p-8 text-center" data-id="element-542">
                  <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-md mb-4 text-coral" data-id="element-543">
                    <MessageCircle size={32} data-id="element-544" />
                  </div>
                  <h4 className="font-bold text-lg mb-2" data-id="element-545">
                    {t('conflictCoaching.questionsTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm mb-4" data-id="element-546">
                    {t('conflictCoaching.questionsDesc')}
                  </p>
                  <Link to="/contact" className="text-coral font-bold hover:underline" data-id="element-547">
                    {t('conflictCoaching.contactLink')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-548">
          <Animate data-id="element-549">
            <Card className="p-12 bg-gradient-to-br from-coral/5 to-orange-50 border-0" data-id="element-550">
              <h2 className="text-3xl font-bold mb-4" data-id="element-551">
                {t('conflictCoaching.ctaTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-552">
                {t('conflictCoaching.ctaDesc')}
              </p>
              <Link to="/contact" data-id="element-553">
                <Button variant="primary" rightIcon={<ArrowRight size={18} data-id="element-555" />} data-id="element-554">
                  {t('conflictCoaching.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}